// 분출은 화면 섬광 없이 0.2초에 걸쳐 솟는 불기둥이다. 맥동·점멸을 사용하지 않는다.
// D3a: 지역 테마(levels.HazardTheme)에 따라 색만 바뀐다 — ember 불기둥, frost 얼음 가시(청백), venom 독가스(연두), C2 sand 모래 간헐천(금빛), void 공허 기둥(자홍).
import * as THREE from 'three/webgpu';
import { float, mix, positionGeometry, sin, time, uniform, uv } from 'three/tsl';
import { Ev, type GameEvent } from '../shared/events';
import { compose } from '../render/util';

export const ERUPTION_CAPACITY = 16;
/** 테마별 분출 색(아래 → 위, 선형 RGB) */
export const ERUPTION_COLORS: Readonly<Record<string, readonly number[]>> = {
  ember: [1.4, 0.18, 0.006, 0.4, 0.018, 0.003],
  frost: [0.75, 1.25, 1.6, 0.08, 0.28, 0.62],
  venom: [0.55, 1.4, 0.18, 0.06, 0.24, 0.02],
  // C2: sand 모래 간헐천(금빛 → 황토), void 공허 기둥(자홍 → 남보라)
  sand: [1.5, 1.05, 0.42, 0.42, 0.22, 0.06],
  void: [1.1, 0.3, 1.8, 0.12, 0.02, 0.3],
};
export const ERUPTION_TICKS = 54;
export function eruptionEnvelope(age: number): number {
  if (age < 0 || age >= ERUPTION_TICKS) return 0;
  return Math.min(1, age / 12) * Math.min(1, (ERUPTION_TICKS - age) / 24);
}
export class EruptionPool {
  readonly start = new Float64Array(ERUPTION_CAPACITY).fill(-1e9);
  readonly x = new Float32Array(ERUPTION_CAPACITY);
  readonly z = new Float32Array(ERUPTION_CAPACITY);
  readonly radius = new Float32Array(ERUPTION_CAPACITY);
  private next = 0;
  onEvent(e: GameEvent, now: number): void {
    if (e.type !== Ev.HazardErupt) return;
    const i = this.next++ % ERUPTION_CAPACITY;
    this.start[i] = now; this.x[i] = e.x; this.z[i] = e.z; this.radius[i] = e.b;
  }
  reset(): void { this.start.fill(-1e9); this.next = 0; }
}
export class EruptionFx {
  readonly pool = new EruptionPool();
  readonly mesh: THREE.InstancedMesh;
  private readonly hot = uniform(new THREE.Color(1.4, 0.18, 0.006));
  private readonly cold = uniform(new THREE.Color(0.4, 0.018, 0.003));
  private theme = 'ember';
  constructor() {
    const g = new THREE.CylinderGeometry(0.35, 1, 1, 10, 1, true); g.translate(0, 0.5, 0);
    const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide });
    m.colorNode = mix(this.hot, this.cold, uv().y);
    // 작은 공간 무늬가 위로 흐르지만 전체 밝기는 점멸하지 않는다.
    m.opacityNode = float(1).sub(uv().y).mul(sin(uv().x.mul(31).add(positionGeometry.y.mul(4)).sub(time.mul(2))).mul(0.12).add(0.68));
    this.mesh = new THREE.InstancedMesh(g, m, ERUPTION_CAPACITY); this.mesh.frustumCulled = false; this.mesh.count = 0;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage); this.mesh.renderOrder = 4;
  }
  /** 지역 테마 색(맵이 바뀔 때만). 값만 바꿔 셰이더를 다시 만들지 않는다 */
  setTheme(theme: string | undefined): void {
    const t = theme ?? 'ember';
    if (t === this.theme) return;
    this.theme = t;
    const c = ERUPTION_COLORS[t] ?? ERUPTION_COLORS.ember;
    this.hot.value.setRGB(c[0], c[1], c[2]); this.cold.value.setRGB(c[3], c[4], c[5]);
  }
  update(now: number): void {
    let count = 0;
    for (let i = 0; i < ERUPTION_CAPACITY; i++) {
      const k = eruptionEnvelope(now - this.pool.start[i]);
      if (k <= 0) continue;
      const r = this.pool.radius[i] * 0.65;
      compose(this.mesh.instanceMatrix.array as Float32Array, count++ * 16, this.pool.x[i], 0.03, this.pool.z[i], 0, 0, r, 4.5 * k, r);
    }
    this.mesh.count = count; this.mesh.instanceMatrix.needsUpdate = true;
  }
  dispose(): void { this.mesh.geometry.dispose(); (this.mesh.material as THREE.Material).dispose(); this.mesh.dispose(); }
}
